import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import {
  MainWrapper,
  MainContainer,
  CardsContainer,
  NoDataWrapper,
  NoDataTitle,
  NoDataDescription,
  PaginationContainer,
} from "./KnowledgeBases.styled";
import Filters from "./components/Filters";
import {
  CardViewX,
  Typography,
  Skeleton,
  Pagination,
} from "@ann/web-components";
import Icon from "../../../../../../../elements/icon/Icon";
import { useStore, useDebounce } from "../../../../../../../hooks";
import { listKnowledge } from "../../../../../Knowledge/services/knowledge";
import TrainModal from "../../../../../Knowledge/components/TrainModal";
import UploadFactsModal from "../../../../../Knowledge/components/UploadFactsModal";
import AIMSDriveModal from "../../../../../Knowledge/components/AIMSDriveModal";
import FactsTrainingModal from "../../../../../Knowledge/pages/KnowledgeHub/components/FactsTrainingModal";
import { getModifiedFromDate } from "../../../../../../../utils/dateUtils";

// Helper function
const transformKnowledgeBase = (apiItem, departmentNameFromStore) => {
  const knowledgebaseUuid =
    apiItem.knowledgebase_uuid || apiItem.knowledgebaseUuid;
  const departmentUuid = apiItem.department_uuid || apiItem.departmentUuid;
  const ownerUuid = apiItem.owner_uuid || apiItem.ownerUuid;
  const countMembers = apiItem.count_members || apiItem.countMembers || 0;
  const countFacts = apiItem.count_facts || apiItem.countFacts || 0;

  const summary = apiItem.summary || {};
  const updatedAt = summary.updated_at || summary.updatedAt;

  // If API returns owner name, use it; otherwise use "Unknown"
  const ownerName =
    apiItem.owner_name ||
    apiItem.ownerName ||
    apiItem.owner?.fullName ||
    "Unknown";

  // Get department name from API response first, fallback to store, then "Unknown"
  const departmentName =
    apiItem.department_name ||
    apiItem.departmentName ||
    departmentNameFromStore ||
    "Unknown";

  return {
    id: knowledgebaseUuid,
    name: apiItem.name || "",
    description: apiItem.description || "",
    owner: ownerName,
    collaborators: countMembers,
    documents: countFacts,
    category: departmentName,
    status:
      apiItem.status?.toLowerCase() === "archived" ? "archived" : "active",
    lastModified: updatedAt
      ? new Date(updatedAt).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    updatedAt: updatedAt || new Date().toISOString(),
    knowledgebase_uuid: knowledgebaseUuid,
    knowledgebaseUuid: knowledgebaseUuid,
    owner_uuid: ownerUuid,
    ownerUuid: ownerUuid,
    department_uuid: departmentUuid,
    departmentUuid: departmentUuid,
  };
};

const KnowledgeBases = observer(({ departmentId }) => {
  const navigate = useNavigate();
  const { departmentsStore } = useStore();
  const [loading, setLoading] = useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedModified, setSelectedModified] = useState(null);
  const [orderBy, setOrderBy] = useState("name");
  const [orderDirection, setOrderDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [meta, setMeta] = useState(null);
  const [isTrainModalOpen, setIsTrainModalOpen] = useState(false);
  const [isUploadFactsModalOpen, setIsUploadFactsModalOpen] = useState(false);
  const [isAIMSDriveModalOpen, setIsAIMSDriveModalOpen] = useState(false);
  const [isFactsTrainingModalOpen, setIsFactsTrainingModalOpen] =
    useState(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);

  // Debounce search keyword
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);
  const isSearching = searchKeyword !== debouncedSearchKeyword;

  // Fetch knowledge bases filtered by department
  const fetchKnowledgeBases = useCallback(async () => {
    if (!departmentId) return;

    setLoading(true);
    try {
      // Get department name from store as fallback (in case API doesn't return it)
      const department = departmentsStore.currentDepartment;
      const departmentNameFromStore =
        department?.department_name || department?.departmentName || "Unknown";

      const filters = {
        department_uuid: departmentId,
      };

      const modifiedValue = selectedModified?.[0];
      if (modifiedValue) {
        const modifiedFrom = getModifiedFromDate(modifiedValue);
        if (modifiedFrom) {
          filters.modified_from = modifiedFrom;
        }
      }

      const ownerValue = selectedOwner?.[0];
      if (ownerValue) {
        filters.user_uuid = ownerValue;
      }

      // Map orderBy to API field names
      const orderByMap = {
        name: "name",
        created: "created_at",
        modified: "updated_at",
      };
      const apiOrderBy = orderByMap[orderBy] || "updated_at";
      const apiOrderDirection =
        orderDirection.toUpperCase() === "ASC" ? "ASC" : "DESC";

      // Prepare search parameter if keyword exists
      const searchParam = debouncedSearchKeyword?.trim()
        ? { search: { keyword: debouncedSearchKeyword.trim() } }
        : {};

      // Fetch knowledge bases filtered by department
      let knowledgeBasesData = [];
      let responseMeta = null;

      try {
        const response = await listKnowledge({
          page: currentPage,
          page_size: itemsPerPage,
          order_by: apiOrderBy,
          order_direction: apiOrderDirection,
          filters: filters,
          ...searchParam, // Include search if keyword exists
        });

        if (response.body && Array.isArray(response.body)) {
          knowledgeBasesData = response.body;
        }

        if (response.meta) {
          responseMeta = response.meta;
        }
      } catch (apiError) {
        console.error("Error fetching from API:", apiError);
        knowledgeBasesData = [];
      }

      // Transform knowledge bases - API department name takes priority, fallback to store
      let transformed = knowledgeBasesData.map((item) =>
        transformKnowledgeBase(item, departmentNameFromStore)
      );

      if (
        selectedOwner &&
        Array.isArray(selectedOwner) &&
        selectedOwner.length > 0
      ) {
        const ownerUuid = selectedOwner[0];
        const filteredByOwner = transformed.filter((kb) => {
          const kbOwnerUuid = kb.owner_uuid || kb.ownerUuid;
          return kbOwnerUuid === ownerUuid;
        });
        transformed = filteredByOwner;
      }

      setKnowledgeBases(transformed);
      setMeta(responseMeta);
    } catch (error) {
      console.error("Error fetching knowledge bases:", error);
      setKnowledgeBases([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [
    departmentId,
    currentPage,
    itemsPerPage,
    orderBy,
    orderDirection,
    debouncedSearchKeyword,
    selectedOwner,
    selectedModified,
    departmentsStore.currentDepartment,
  ]);

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  // Reset to page 1 when filters or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchKeyword,
    selectedOwner,
    selectedModified,
    orderBy,
    orderDirection,
  ]);

  // Client-side filtering removed - search is now handled by the backend API

  const getStatusTag = (status) =>
    status === "archived"
      ? { label: "Archived", type: "neutral" }
      : { label: "Active", type: "success" };

  const handleManage = useCallback(
    (knowledgeBaseId, knowledgeBaseName) => {
      navigate(`/v2/knowledge/knowledge-hub/${knowledgeBaseId}`, {
        state: { knowledgeName: knowledgeBaseName },
      });
    },
    [navigate]
  );

  const handleTrain = useCallback((knowledgeBase) => {
    setSelectedKnowledgeBase(knowledgeBase);
    setIsTrainModalOpen(true);
  }, []);

  const handleTrainModalClose = useCallback(() => {
    setIsTrainModalOpen(false);
    setSelectedKnowledgeBase(null);
  }, []);

  const handleUploadDocuments = useCallback(() => {
    setIsTrainModalOpen(false);
    setIsUploadFactsModalOpen(true);
  }, []);

  const handleUploadFactsModalClose = useCallback(() => {
    setIsUploadFactsModalOpen(false);
    setIsTrainModalOpen(true);
  }, []);

  const handleAIMSDrive = useCallback(() => {
    setIsTrainModalOpen(false);
    setIsAIMSDriveModalOpen(true);
  }, []);

  const handleAIMSDriveModalClose = useCallback(() => {
    setIsAIMSDriveModalOpen(false);
    setIsTrainModalOpen(true);
  }, []);

  const handleContinueInterview = useCallback(() => {
    setIsTrainModalOpen(false);
    setIsFactsTrainingModalOpen(true);
  }, []);

  const handleFactsTrainingModalClose = useCallback(() => {
    setIsFactsTrainingModalOpen(false);
    setIsTrainModalOpen(true);
  }, []);

  const handleUploadStart = useCallback(() => {
    setIsUploadFactsModalOpen(false);
    setIsTrainModalOpen(false);
    fetchKnowledgeBases(); // Refresh list after upload
  }, [fetchKnowledgeBases]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((itemsPerPage) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1);
  }, []);

  return (
    <MainWrapper>
      <Filters
        onSearchChange={setSearchKeyword}
        onOwnerChange={setSelectedOwner}
        onModifiedChange={setSelectedModified}
        onSortChange={setOrderBy}
        onOrderChange={setOrderDirection}
        selectedSortOption={orderBy}
        orderDirection={orderDirection}
        loading={loading}
        selectedOwner={selectedOwner}
        selectedModified={selectedModified}
        searchKeyword={searchKeyword}
      />
      <MainContainer>
        {loading || isSearching ? (
          <CardsContainer>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={`skeleton-${index}`} height="280px" width="100%" />
            ))}
          </CardsContainer>
        ) : knowledgeBases.length === 0 ? (
          <NoDataWrapper>
            <Icon name="noDataAvailable" width="201px" height="195px" />
            <NoDataTitle>No knowledge bases found</NoDataTitle>
            <NoDataDescription>
              {selectedOwner?.[0] ||
              selectedModified?.[0] ||
              searchKeyword?.trim()
                ? "No knowledge bases match your current filters. Try adjusting your search or filters."
                : "No knowledge bases found for this department."}
            </NoDataDescription>
          </NoDataWrapper>
        ) : (
          <CardsContainer>
            {knowledgeBases.map((knowledgeBase) => (
              <CardViewX
                key={knowledgeBase.id}
                size="medium"
                showHover
                onCheckboxChange={() => {}}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
              >
                <CardViewX.Header
                  tag={{
                    icon:
                      knowledgeBase.status === "archived"
                        ? "archive"
                        : "checkCircle",
                    label: getStatusTag(knowledgeBase.status).label,
                    type: getStatusTag(knowledgeBase.status).type,
                  }}
                  title={knowledgeBase.name}
                />
                <Typography
                  category="body"
                  size="small"
                  weight="regular"
                  color="body"
                >
                  {knowledgeBase.category}
                </Typography>
                <CardViewX.Meta>
                  <CardViewX.MetaItem
                    icon="person"
                    label={knowledgeBase.owner}
                    tooltipContent="Owner"
                    color="primary-alt"
                  />
                  <CardViewX.MetaItem
                    icon="group"
                    label={`+${knowledgeBase.collaborators}`}
                    tooltipContent="Collaborators"
                    color="yellow"
                  />
                  <CardViewX.MetaItem
                    icon="description"
                    label={`${knowledgeBase.documents} documents`}
                    tooltipContent="Total documents"
                    color="lightBlue"
                  />
                </CardViewX.Meta>
                <CardViewX.Actions>
                  <CardViewX.SecondaryAction
                    label="Train"
                    onClick={() => handleTrain(knowledgeBase)}
                  />
                  <CardViewX.PrimaryAction
                    label="Manage"
                    onClick={() =>
                      handleManage(knowledgeBase.id, knowledgeBase.name)
                    }
                  />
                </CardViewX.Actions>
              </CardViewX>
            ))}
          </CardsContainer>
        )}
        {knowledgeBases.length > 0 && meta && (
          <PaginationContainer>
            <Pagination
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              onPageChange={handlePageChange}
              totalItems={meta.total_items || meta.totalItems || 0}
              totalPages={meta.total_pages || meta.totalPages || 1}
            />
          </PaginationContainer>
        )}
      </MainContainer>
      <TrainModal
        isOpen={isTrainModalOpen}
        onClose={handleTrainModalClose}
        knowledgeBase={selectedKnowledgeBase}
        onUploadDocuments={handleUploadDocuments}
        onAIMSDrive={handleAIMSDrive}
        onContinueInterview={handleContinueInterview}
      />
      <FactsTrainingModal
        isOpen={isFactsTrainingModalOpen}
        onClose={handleFactsTrainingModalClose}
      />
      <UploadFactsModal
        isOpen={isUploadFactsModalOpen}
        onClose={handleUploadFactsModalClose}
        knowledgeBase={selectedKnowledgeBase}
        onUploadStart={handleUploadStart}
      />
      <AIMSDriveModal
        isOpen={isAIMSDriveModalOpen}
        onClose={handleAIMSDriveModalClose}
        knowledgeBase={selectedKnowledgeBase}
      />
    </MainWrapper>
  );
});

export default KnowledgeBases;
